import React, { useContext, useState } from "react";
import { Alert, Button, Card, Col, Form, Image, InputGroup, Row, Spinner } from "react-bootstrap";
import { FaCheckCircle, FaPaperPlane, FaSearch, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { Context } from "../../index";
import {
    confirmBookTransferApi,
    fetchCurrentUserBooks,
    lookupBookTransferApi,
    requestBookTransferApi
} from "../../http/bookAPI";

const getErrorMessage = (error, fallback = "Something went wrong. Please try again.") => (
    error?.response?.data?.message || error?.message || fallback
);

const coverUrl = (coverFile) => (
    coverFile
        ? `${process.env.REACT_APP_API_URL}covers/${coverFile}`
        : `${process.env.REACT_APP_API_URL}covers/empty-cover.jpg`
);

const formatFullDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return new Intl.DateTimeFormat("en-GB", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
    }).format(date);
};

const TransferBookFromAnotherAccount = () => {
    const { user, book } = useContext(Context);
    const [step, setStep] = useState("enterId");
    const [bookId, setBookId] = useState("");
    const [preview, setPreview] = useState(null);
    const [transferId, setTransferId] = useState(null);
    const [expiresAt, setExpiresAt] = useState(null);
    const [sumCode, setSumCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const resetFlow = () => {
        setStep("enterId");
        setBookId("");
        setPreview(null);
        setTransferId(null);
        setExpiresAt(null);
        setSumCode("");
        setError("");
    };

    const goDashboard = () => {
        user?.setActiveComponent("MainPageDashboard");
    };

    const handleLookup = async (event) => {
        event?.preventDefault();
        const id = Number(bookId);

        if (!Number.isInteger(id) || id <= 0) {
            setError("Please enter a valid book/product ID.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const data = await lookupBookTransferApi(id);
            setPreview(data?.book || null);
            setStep("confirmBook");
        } catch (err) {
            setError(getErrorMessage(err, "Book/product was not found or cannot be transferred."));
        } finally {
            setLoading(false);
        }
    };

    const handleSendPins = async () => {
        if (!preview?.id) return;

        setLoading(true);
        setError("");

        try {
            const data = await requestBookTransferApi(preview.id);
            setTransferId(data?.transferId);
            setExpiresAt(data?.expiresAt);
            setStep("enterCode");
            toast.success("PIN codes were sent to both account emails.", {
                position: "bottom-center",
                autoClose: 2500,
                theme: "colored",
            });
        } catch (err) {
            setError(getErrorMessage(err, "Failed to send transfer PIN codes."));
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async (event) => {
        event?.preventDefault();

        if (!transferId) {
            setError("Transfer request is missing. Please request PIN codes again.");
            return;
        }

        if (!sumCode || !Number.isFinite(Number(sumCode))) {
            setError("Please enter the mathematical sum of both PIN codes.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const result = await confirmBookTransferApi(transferId, Number(sumCode));
            const transferredBook = result?.book
                ? {
                    ...result.book,
                    createdAt: result.book.createdAt || new Date().toISOString(),
                }
                : preview;

            if (transferredBook?.id) {
                const currentBooks = Array.isArray(book?.currentuserbooks) ? book.currentuserbooks : [];
                const mergedBooks = [
                    transferredBook,
                    ...currentBooks.filter((item) => String(item.id) !== String(transferredBook.id)),
                ];
                book?.setCurrentUserBooks(mergedBooks);
                book?.setSelectedCurrentUserBook(transferredBook);
            }

            if (user?.user?.id) {
                const data = await fetchCurrentUserBooks(user.user.id, { _ts: Date.now() });
                book?.setCurrentUserBooks(data);
                const fullTransferredBook = Array.isArray(data)
                    ? data.find((item) => String(item.id) === String(transferredBook?.id))
                    : null;
                if (fullTransferredBook) {
                    book?.setSelectedCurrentUserBook(fullTransferredBook);
                }
            }
            setStep("success");
            user?.setActiveComponent("BookFull");
            toast.success("Book/Product transfer completed.", {
                position: "bottom-center",
                autoClose: 2500,
                theme: "colored",
            });
        } catch (err) {
            setError(getErrorMessage(err, "Transfer confirmation failed."));
        } finally {
            setLoading(false);
        }
    };

    const renderPreview = () => (
        <div className="border rounded-1 border-secondary p-2 mb-2 small">
            <Row className="g-3 align-items-start">
                <Col xs={12} md={2} className="d-flex justify-content-center align-items-start">
                    <Image
                        src={coverUrl(preview?.cover_file)}
                        alt="Book Cover"
                        style={{
                            width: "100%",
                            maxWidth: "160px",
                            objectFit: "cover",
                            borderRadius: "8px",
                        }}
                        className="shadow mb-0 mx-auto d-block"
                        onError={(event) => {
                            event.currentTarget.src = `${process.env.REACT_APP_API_URL}covers/empty-cover.jpg`;
                        }}
                    />
                </Col>
                <Col xs={12} md={10} className="text-muted">
                    <div><strong>Book/Product ID:</strong> {preview?.id}</div>
                    <div><strong>Title:</strong> {preview?.title || "Untitled"}</div>
                    {preview?.subtitle && <div><strong>Subtitle:</strong> {preview.subtitle}</div>}
                    {preview?.author && <div><strong>Author:</strong> {preview.author}</div>}
                    <div><strong>Current owner:</strong> {preview?.ownerEmailMasked || "masked"}</div>
                </Col>
            </Row>
        </div>
    );

    return (
        <Card className="p-3 w-100 mt-2">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center flex-wrap gap-2 w-100">
                <h5 className="text-muted mb-2 mb-md-0">
                    Transfer a Book/Product from Another Virtalibry Account:
                </h5>
                <Button
                    className="d-inline-flex align-items-center border btn-secondary btn-sm"
                    size="sm"
                    onClick={goDashboard}
                >
                    <i className="me-2 fas fa-arrow-circle-left"></i> Back
                </Button>
            </div>

            <div className="text-muted small">
                <p className="mb-1 mt-2">
                    Use this flow when a book/product was already added under another Virtalibry account and you want to move it into your current account.
                </p>
                <p className="mb-1">
                    <strong>Step 1:</strong> Enter the book/product ID and confirm that the preview is the correct item.
                </p>
                <p className="mb-1">
                    <strong> Step 2:</strong> Get PIN codes on both account emails, add the two PIN numbers together, and enter the result to complete the transfer.
                </p>
            </div>

            <Col md={12} className="mt-2">
                <Card className="mt-2 mb-2 border rounded-1 border-secondary">
                    <Card.Header className="py-1 small">Transfer from Another Account. Insert ID, verify, then confirm.</Card.Header>
                    <Card.Body className="pt-2">
                    {error && (
                        <Alert variant="danger" className="small">
                            {error}
                        </Alert>
                    )}

                    {step === "enterId" && (
                        <>
                            <Form onSubmit={handleLookup} className="small">
                                <InputGroup className="mb-2 mt-2 border rounded-1 border-secondary" size="sm">
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        value={bookId}
                                        onChange={(event) => setBookId(event.target.value)}
                                        placeholder="Book/Product ID"
                                        disabled={loading}
                                        size="sm"
                                        className="border"
                                    />
                                    <Button
                                        type="submit"
                                        variant="success"
                                        disabled={loading}
                                        size="sm"
                                        className="d-flex align-items-center justify-content-center"
                                    >
                                        {loading ? <Spinner size="sm" /> : <FaSearch className="me-2" />}
                                        Check
                                    </Button>
                                </InputGroup>
                                <div className="text-muted">
                                    Enter the numeric ID of the book/product that currently belongs to another account.
                                </div>
                            </Form>
                        </>
                    )}

                    {step === "confirmBook" && preview && (
                        <>
                            <h6 className="text-muted mb-2">Is this the book/product?</h6>
                            {renderPreview()}
                            <div className="d-flex flex-wrap gap-2 mt-3">
                                <Button variant="success" size="sm" onClick={() => setStep("instructions")}>
                                    <FaCheckCircle className="me-2" />
                                    Yes, continue
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={resetFlow}>
                                    <FaTimes className="me-2" />
                                    No, clear fields
                                </Button>
                            </div>
                        </>
                    )}

                    {step === "instructions" && preview && (
                        <>
                            <h6 className="text-muted mb-2">Before sending PIN codes</h6>
                            {renderPreview()}
                            <div className="text-muted small mt-2 mb-2">
                                You will need access to both inboxes:
                                <br />
                                <strong>1.</strong> The source account email where the book/product currently lives.
                                <br />
                                <strong>2.</strong> The current account email where you want to move it.
                                <br />
                                After you receive both PIN codes, calculate the arithmetic sum of the two numbers and enter the result here.
                                Example: if the source account PIN is 123456 and the destination account PIN is 111111, enter 234567.
                                The PIN codes are valid for 10 minutes.
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                <Button variant="success" size="sm" onClick={handleSendPins} disabled={loading}>
                                    {loading ? <Spinner size="sm" className="me-2" /> : <FaPaperPlane className="me-2" />}
                                    Send PIN codes
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={() => setStep("confirmBook")} disabled={loading}>
                                    Back
                                </Button>
                            </div>
                        </>
                    )}

                    {step === "enterCode" && preview && (
                        <>
                            <h6 className="text-muted mb-2">Enter the sum of both PIN codes</h6>
                            {renderPreview()}
                            <div className="text-muted small mt-2 mb-2">
                                PIN codes were sent to both account emails. Add both PINs mathematically and enter the sum.
                                {expiresAt && (
                                    <div className="mt-1">
                                        Valid until: {formatFullDateTime(expiresAt)}
                                    </div>
                                )}
                            </div>
                            <Form onSubmit={handleConfirm} className="small">
                                <InputGroup className="mb-2 mt-2 border rounded-1 border-secondary" size="sm">
                                    <Form.Control
                                        type="number"
                                        value={sumCode}
                                        onChange={(event) => setSumCode(event.target.value)}
                                        placeholder="Sum of both PIN codes"
                                        disabled={loading}
                                        size="sm"
                                        className="border"
                                    />
                                    <Button
                                        type="submit"
                                        variant="success"
                                        disabled={loading}
                                        size="sm"
                                        className="d-flex align-items-center justify-content-center"
                                    >
                                        {loading ? <Spinner size="sm" /> : <FaCheckCircle className="me-2" />}
                                        Confirm transfer
                                    </Button>
                                </InputGroup>
                            </Form>
                        </>
                    )}

                    {step === "success" && (
                        <div className="text-muted small">
                            <h6 className="text-success mb-2">Book/Product transfer completed.</h6>
                            <p className="mb-2">
                                The owner was changed to your current account. Both account emails received a transfer memo.
                            </p>
                            <div className="d-flex gap-2 flex-wrap">
                                <Button variant="success" size="sm" onClick={goDashboard}>
                                    Back to dashboard
                                </Button>
                                <Button variant="outline-success" size="sm" onClick={resetFlow}>
                                    Transfer another item
                                </Button>
                            </div>
                        </div>
                    )}
                    </Card.Body>
                </Card>
            </Col>
        </Card>
    );
};

export default TransferBookFromAnotherAccount;
